import { Link } from 'react-router-dom';
import { EMPLOYEE_PROFILE_STEPS } from '../../people/types';
import type { CheckBatch, CheckBatchLine } from '../types';
import { formatMoney, NOT_AVAILABLE } from '../format';

export interface CheckNumberEdit {
  lineId: string;
  value: string;
  error: string | null;
  saving: boolean;
}

interface CheckTableProps {
  batch: CheckBatch;
  selectedIds: ReadonlySet<string>;
  edit: CheckNumberEdit | null;
  disabled: boolean;
  onToggle: (lineId: string) => void;
  onToggleAll: () => void;
  onStartEdit: (line: CheckBatchLine) => void;
  onChangeEdit: (value: string) => void;
  onCommitEdit: () => void;
  onCancelEdit: () => void;
  onRemoveRow: (line: CheckBatchLine) => void;
}

function StatusChip({ batch, line }: { batch: CheckBatch; line: CheckBatchLine }) {
  // Until a readiness check has run for the current version nothing is known, so nothing is claimed ("Ready" would be a guess).
  if (!batch.readiness || batch.readiness.isStale) return <span className="wc-chip wc-chip--neutral">Not checked yet</span>;
  return line.rowStatus === 'NeedsAttention'
    ? <span className="wc-chip wc-chip--bad">Needs Attention</span>
    : <span className="wc-chip wc-chip--ok">Ready</span>;
}

export function CheckTable(props: CheckTableProps) {
  const { batch, selectedIds, edit, disabled } = props;
  const lines = batch.lines.filter((l) => l.included);
  const removed = batch.lines.length - lines.length;
  const allSelected = lines.length > 0 && lines.every((l) => selectedIds.has(l.id));

  if (lines.length === 0) {
    return (
      <p className="wc-empty" role="status">
        No employees are left in this batch{removed > 0 ? ` (${removed} removed from this batch)` : ''}. Go back to the Custom Pay Run to change who is paid by check.
      </p>
    );
  }

  return (
    <>
      <div className="table-scroll" role="region" aria-label="Employees paid by check" tabIndex={0}>
        <table className="wc-table" aria-label="Employees paid by check">
          <thead>
            <tr>
              <th scope="col">
                <input type="checkbox" aria-label="Select all employees in this batch" checked={allSelected} disabled={disabled} onChange={props.onToggleAll} />
              </th>
              <th scope="col">Employee</th>
              <th scope="col">Branch</th>
              <th scope="col">Discipline/Role</th>
              <th scope="col">Pay Type</th>
              <th scope="col">Earnings</th>
              <th scope="col" className="wc-num">Gross Pay</th>
              <th scope="col" className="wc-num">Check Amount</th>
              <th scope="col">Check Number</th>
              <th scope="col">Status</th>
              <th scope="col">Action</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => {
              const editing = edit?.lineId === line.id;
              return (
                <tr key={line.id} data-testid={`check-row-${line.id}`}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Select ${line.employeeName}`}
                      checked={selectedIds.has(line.id)}
                      disabled={disabled}
                      onChange={() => props.onToggle(line.id)}
                    />
                  </td>
                  <td>{line.employeeName}<span className="wc-sub">{line.employeeNumber}</span></td>
                  <td className="wc-wrap">{line.branchName ?? NOT_AVAILABLE}</td>
                  <td className="wc-wrap">{line.roleName ?? NOT_AVAILABLE}</td>
                  <td className="wc-wrap">{line.payType ?? NOT_AVAILABLE}</td>
                  <td className="wc-wrap">{line.earningsSummary ?? NOT_AVAILABLE}</td>
                  <td className="wc-num">{formatMoney(line.grossPay)}</td>
                  <td className="wc-num wc-amount">{formatMoney(line.checkAmount)}</td>
                  <td>
                    {editing ? (
                      <form
                        className="wc-edit"
                        onSubmit={(e) => {
                          e.preventDefault();
                          props.onCommitEdit();
                        }}
                        onKeyDown={(e) => {
                          // Escape cancels from anywhere in the editor (the input, Save or Cancel), not only from the input.
                          if (e.key === 'Escape') {
                            e.stopPropagation();
                            props.onCancelEdit();
                          }
                        }}
                      >
                        <input
                          aria-label={`Check number for ${line.employeeName}`}
                          aria-invalid={edit.error ? true : undefined}
                          aria-describedby={edit.error ? `check-number-error-${line.id}` : undefined}
                          value={edit.value}
                          maxLength={20}
                          disabled={edit.saving}
                          data-autofocus
                          autoFocus
                          onChange={(e) => props.onChangeEdit(e.target.value)}
                        />
                        <button type="submit" className="wc-btn wc-btn--mini" disabled={edit.saving}>Save</button>
                        <button type="button" className="wc-btn wc-btn--mini wc-btn--ghost" disabled={edit.saving} onClick={props.onCancelEdit}>Cancel</button>
                        {edit.error && <span id={`check-number-error-${line.id}`} className="error wc-edit__error" role="alert">{edit.error}</span>}
                      </form>
                    ) : (
                      <span className="wc-checknum">
                        <span data-testid={`check-number-${line.id}`}>{line.checkNumber}</span>
                        <button
                          type="button"
                          className="wc-icon-btn"
                          aria-label={`Edit check number for ${line.employeeName}`}
                          disabled={disabled}
                          onClick={() => props.onStartEdit(line)}
                        >
                          ✎
                        </button>
                      </span>
                    )}
                  </td>
                  <td><StatusChip batch={batch} line={line} /></td>
                  <td>
                    <span className="wc-actions">
                      <Link
                        className="wc-icon-btn wc-icon-btn--view"
                        aria-label={`View ${line.employeeName}`}
                        to={`/people/${line.employeeId}/${EMPLOYEE_PROFILE_STEPS[0].key}`}
                      >
                        👁
                      </Link>
                      <button
                        type="button"
                        className="wc-icon-btn wc-icon-btn--remove"
                        aria-label={`Remove ${line.employeeName} from this batch`}
                        disabled={disabled}
                        onClick={() => props.onRemoveRow(line)}
                      >
                        🗑
                      </button>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {removed > 0 && (
        <p className="wc-muted" data-testid="removed-note">
          {removed} {removed === 1 ? 'employee was' : 'employees were'} removed from this batch. Their employee records and payroll history are unchanged.
        </p>
      )}
    </>
  );
}
